"use client";

import { businessFiltersAtom } from "@/app/(saas)/(app)/businesses/_components/filters/state";
import { useListingFacetCounts } from "@/app/(saas)/(app)/businesses/_components/filters/use-listing-facet-counts";
import { Checkbox } from "@/components/ui/checkbox";
import { ListingStatusBadge } from "@/components/listings/listing-status-badge";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  PUBLICLY_FILTERABLE_LISTING_STATUSES,
  type PubliclyFilterableListingStatus,
} from "@repo/utils";
import { useAtom } from "jotai";

export function BusinessStatusFilter() {
  const [filters, setFilters] = useAtom(businessFiltersAtom);
  const facets = useListingFacetCounts();

  function toggleStatus(status: PubliclyFilterableListingStatus) {
    const alreadySelected = filters.statuses.includes(status);
    setFilters((prev) => ({
      ...prev,
      statuses: alreadySelected
        ? prev.statuses.filter((item) => item !== status)
        : [...prev.statuses, status],
    }));
  }

  function resetStatuses() {
    setFilters((prev) => ({
      ...prev,
      statuses: [],
    }));
  }

  const hasData = facets.data != null;

  return (
    <div className="max-h-full overflow-y-auto md:max-h-[500px]">
      {filters.statuses.length > 0 && (
        <div className="flex items-center px-3 py-2">
          <span className="text-xs font-medium text-muted-foreground">
            {filters.statuses.length} selected
          </span>
        </div>
      )}

      <div className="flex flex-col">
        {PUBLICLY_FILTERABLE_LISTING_STATUSES.map((status, idx) => (
          <StatusRow
            key={status}
            status={status}
            count={hasData ? (facets.data?.statusCounts[status] ?? 0) : undefined}
            isLoading={facets.isLoading}
            isSelected={filters.statuses.includes(status)}
            onToggle={() => toggleStatus(status)}
            showDivider={idx < PUBLICLY_FILTERABLE_LISTING_STATUSES.length - 1}
          />
        ))}
      </div>

      <div className="flex items-center justify-between border-t px-3 py-2">
        <button
          type="button"
          className="text-xs text-muted-foreground transition-colors hover:text-foreground hover:underline"
          onClick={resetStatuses}
        >
          Reset
        </button>

        <span className="text-xs text-muted-foreground">
          {facets.isLoading ? (
            <span className="inline-block h-3 w-8 animate-pulse rounded bg-muted" />
          ) : (
            <span className="font-data tabular-nums text-foreground">
              {facets.data?.total ?? 0}
            </span>
          )}{" "}
          businesses match
        </span>
      </div>
    </div>
  );
}

function StatusRow({
  status,
  count,
  isLoading,
  isSelected,
  onToggle,
  showDivider,
}: {
  status: PubliclyFilterableListingStatus;
  count?: number;
  isLoading: boolean;
  isSelected: boolean;
  onToggle: () => void;
  showDivider: boolean;
}) {
  return (
    <>
      <button
        type="button"
        className={cn(
          "flex min-h-11 md:min-h-10 w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          isSelected && "bg-muted/30",
        )}
        onClick={onToggle}
        aria-pressed={isSelected}
      >
        <div className="min-w-0 flex-1">
          <ListingStatusBadge status={status} small />
        </div>

        <div className="min-w-[2ch] text-right font-data text-xs tabular-nums text-muted-foreground">
          {isLoading ? (
            <span className="inline-block w-6 h-3 bg-muted animate-pulse rounded" />
          ) : (
            count ?? 0
          )}
        </div>

        <Checkbox
          checked={isSelected}
          className="w-5 h-5 border-2 rounded-[6px] shrink-0 pointer-events-none"
        />
      </button>

      {showDivider && <Separator />}
    </>
  );
}
